import { TOP_LEVEL_TOT_TEST_IDS } from '@/top-level-tot/constants';
import {
  BooleanDict,
  RSuiteWhisperSpeakerRenderFn,
  TopLevelTOTFilters,
} from '@/top-level-tot/types';
import {
  additionalFilterConfigs,
  auditingFilter,
  interestFilter,
  penaltiesFilter,
  removeZeroTaxableReceipts,
  reportingStatusFilter,
} from '@/top-level-tot/utils/ui-filters-utils';
import kebabCase from 'lodash/kebabCase';
import { useCallback, useState } from 'react';
import { Button, Checkbox, Whisper } from 'rsuite';

type Props = {
  onFiltersChange?: (value: TopLevelTOTFilters) => void;
};

export function AdditionalFilters(props: Props) {
  const { onFiltersChange } = props;

  const [additionalFilters, setAdditionalFilters] = useState<BooleanDict>({});

  const handleAdditionalFiltersChange = useCallback(
    function (filters: BooleanDict): void {
      setAdditionalFilters(filters);

      if (!onFiltersChange) {
        return;
      }

      const externalFilters: TopLevelTOTFilters = {
        reportingStatus: reportingStatusFilter(filters),
        penalties: penaltiesFilter(filters),
        interest: interestFilter(filters),
        removeZeroTaxableReceipts: filters[removeZeroTaxableReceipts],
        auditing: auditingFilter(filters),
      };
      onFiltersChange(externalFilters);
    },
    [onFiltersChange, setAdditionalFilters]
  );

  const switchFilter = useCallback(
    (name: string, checked: boolean) => {
      handleAdditionalFiltersChange({
        ...additionalFilters,
        [name]: checked,
      });
    },
    [additionalFilters, handleAdditionalFiltersChange]
  );

  const overlayUI: RSuiteWhisperSpeakerRenderFn = (style, ref, className) => (
    <div
      data-testid={
        TOP_LEVEL_TOT_TEST_IDS.PAYMENT_PERIOD_ADDITIONAL_FILTERS_POPUP
      }
      ref={ref}
      className={`absolute min-w-[200px] border-2 z-40 bg-white ${className}`}
      style={style}
    >
      <div className="flex gap-2 p-2">
        {additionalFilterConfigs.map(({ groupName, filters }) => (
          <div key={kebabCase(groupName)}>
            <strong
              data-testid={`additional-filter-label-${kebabCase(groupName)}`}
              className="pl-[10px]"
            >
              {groupName}
            </strong>

            <div className="flex flex-col">
              {filters.map((name) => (
                <Checkbox
                  key={kebabCase(name)}
                  data-testid={`additional-filter-checkbox-${kebabCase(name)}`}
                  checked={additionalFilters[name]}
                  onChange={(_, checked) => switchFilter(name, checked)}
                >
                  {name}
                </Checkbox>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Whisper
      trigger="click"
      placement="bottom"
      speaker={(props, ref) => {
        // eslint-disable-next-line react/prop-types
        const { className, left, top } = props;
        return overlayUI({ left, top }, ref, className);
      }}
    >
      <Button
        data-testid={TOP_LEVEL_TOT_TEST_IDS.PAYMENT_PERIOD_ADDITIONAL_FILTERS}
      >
        Additional Filters
      </Button>
    </Whisper>
  );
}
